import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { DataListDefault, DataListResponse, WorkTab } from "types/ui/card-item";
import TabsLayout from "components/tabs-layout";
import BoardBackground from "components/board/board-bg";
import Table, { tableHeader } from "components/table";
import { IconFolderOpen } from "@tabler/icons-react";
import ActionButton from "components/table/action-btn";
import { oneLine } from "components/table/table-parts";
import { DocumentListResponse, DocumentProps } from "types/document";
import { useSharedState } from "state";
import { StatusEnum } from "types/dict";

export default function DocumentsPage() {
  const {
    documents,
    getDocumentsData,
    documentsTable,
    getDocumentsTableData,
    currentPageTab,
    searchQuery,
    handleSearch,
    setOffset,
    firstEntry,
  } = useSharedState();
  const navigate = useNavigate();

  const [documentCards, setDocumentCards] = useState<DataListResponse>(DataListDefault);

  useEffect(() => {
    updateDocumentsList();
  }, [currentPageTab]);

  useEffect(() => {
    setDocumentCards(convertDocumentsToCards(documents));
  }, [documents]);

  const updateDocumentsList = async () => {
    currentPageTab === "cards" && (await getDocumentsData());
    currentPageTab === "table" && (await getDocumentsTableData());
  };

  const onDocumentPageChange = async (offset: number) => {
    setOffset(offset);
    await handleSearch(searchQuery, undefined, offset);
  };

  const formattedCards = (documentsByStatus: DocumentProps[]) => {
    return documentsByStatus?.map((document) => ({
      id: document.document_request_id,
      title: document.card_description,
      description: document.description,
      status: document.status,
      dueDate: document.request_date,
      created_by: document.creator_name,
      assigned_to: document.approver_name,
      priority: undefined,
    }));
  };

  const convertDocumentsToCards = (documents: DocumentListResponse): DataListResponse => {
    return {
      "0": {
        data: formattedCards(documents["0"]?.data),
        total_count: documents["0"]?.total_count,
      },
      "1": {
        data: formattedCards(documents["1"]?.data),
        total_count: documents["1"]?.total_count,
      },
      "2": {
        data: formattedCards(documents["2"]?.data),
        total_count: documents["2"]?.total_count,
      },
      "3": {
        data: formattedCards(documents["3"]?.data),
        total_count: documents["3"]?.total_count,
      },
    };
  };

  const getStatusTitle = (status: number) => {
    switch (status) {
      case StatusEnum.TO_DO:
        return "К выполнению";
      case StatusEnum.IN_PROGRESS:
        return "В работе";
      case StatusEnum.COMPLETED:
        return "Завершён";
      case StatusEnum.REJECTED:
        return "Отклонён";
      default:
        return "-";
    }
  };

  const documentsTableHeaders: tableHeader[] = [
    { header_text: "№", narrow: true, wrap: false },
    { header_text: "Информация по договору", narrow: false, wrap: false },
    { header_text: "Шаблон", narrow: false, wrap: false },
    { header_text: "Контрагент", narrow: false, wrap: false },
    { header_text: "Номер договора", narrow: false, wrap: false },
    { header_text: "Ответственный", narrow: false, wrap: false },
    { header_text: "Статус", narrow: false, wrap: false },
    { header_text: "Действия", narrow: false, wrap: false },
  ];

  const documentsList = (
    <>
      {documentsTable.data.map((document, index) => (
        <tr key={index}>
          <td>{oneLine((firstEntry + index).toString())}</td>
          <td>{oneLine(document.description || "-")}</td>
          <td>{oneLine(document.template_name || "-")}</td>
          <td>{oneLine(document.contragent || "-")}</td>
          <td>{oneLine(document.lease_number || "-")}</td>
          <td>{oneLine(document.approver_name || "-")}</td>
          <td>{oneLine(getStatusTitle(document.status))}</td>
          <td>
            <div className="btn-list flex-nowrap">
              <ActionButton
                icon={<IconFolderOpen className="icon" />}
                title="Подробнее"
                action={() => navigate(`${document.document_request_id}`)}
                color={"info"}
                modal={false}
              />
            </div>
          </td>
        </tr>
      ))}
    </>
  );

  const documentsTableComponent = Table(documentsTableHeaders, documentsList, true, {
    total: documentsTable.total_count,
    updateInfo: onDocumentPageChange,
  });

  const document_tabs: WorkTab[] = [
    {
      id: "cards",
      title: "Договоры по статусам",
      component: <BoardBackground data={documentCards} />,
    },
    { id: "table", title: "Таблица", component: documentsTableComponent },
  ];

  return (
    <TabsLayout
      tabs={document_tabs}
      header="Договоры на утверждение"
      subheaderText={`Количество договоров: ${documentsTable.total_count}`}
      buttonText="Новый договор"
    />
  );
}
